import _cloneDeep from "lodash.clonedeep";
import Color from "./Color";
import FlowObject from "./FlowObject";
import ImageAsset from "./Assets";
import TextFlowObject from "./TextFlowObject";
import guidGenerator, {
  getCurrentDate,
} from "../features/utils/commonFunctions";
import ShapeFlowObject from "./ShapeFlowObject";
import { FlowObjects, IndexName } from "../features/fsm/types";
import ImageFlowObject from "./ImageFlowObject";
import PdfFlowObject from "./PdfFlowObject";
import VideoFlowObject from "./VideoFlowObject";
import GalleryFlowObject from "./GalleryFlowObject";
import WebFlowObject from "./WebFlowObject";
import * as Sentry from "@sentry/react"

export type LowerBool = "no" | "yes";

export enum OrderShift {
  UP,
  DOWN,
}

export default class Screens {
  FlowScreenModelBackgroundColorKey: Color;
  created: string;
  id: string;
  mixedMediaObjects: FlowObjects[];
  previewScreenShot: ImageAsset;
  previewScreenShotInvalidated: LowerBool;
  previewScreenShot_textless: ImageAsset | null;
  presenterNotes: string;
  slideGestureForNavigationDisabled: boolean;
  title: string;
  type: string;
  updated: string;
  autoAdvanceSlideDuration?: number;
  autoAdvanceSlideTransitionType?: number;
  /**
   * When we replace {@link Screens.previewScreenShot} with Rackspace CDN URL, the image isn't always saturated through Rackspace's network. We keep a fallback image with a base64 encoded image to not show the user this part.
   */
  __fallbackScreenShot: ImageAsset;

  constructor(
    FlowScreenModelBackgroundColorKey = new Color(255, 255, 255, 1),
    created = getCurrentDate(),
    id: string,
    mixedMediaObjects: FlowObjects[],
    previewScreenShot: ImageAsset,
    previewScreenShotInvalidated: LowerBool,
    previewScreenShot_textless: ImageAsset,
    presenterNotes = "",
    slideGestureForNavigationDisabled = false,
    title: string,
    type: string,
    updated = getCurrentDate(),
    __fallbackScreenShot: ImageAsset,
    autoAdvanceSlideDuration?: number,
    autoAdvanceSlideTransitionType?: number
  ) {
    this.FlowScreenModelBackgroundColorKey = FlowScreenModelBackgroundColorKey;
    this.created = created;
    this.id = id;
    this.mixedMediaObjects = mixedMediaObjects;
    this.previewScreenShot = previewScreenShot;
    this.previewScreenShotInvalidated = previewScreenShotInvalidated;
    this.previewScreenShot_textless = previewScreenShot_textless;
    this.slideGestureForNavigationDisabled = slideGestureForNavigationDisabled;
    this.title = title;
    this.type = type;
    this.updated = updated;
    this.presenterNotes = presenterNotes;
    this.autoAdvanceSlideDuration = autoAdvanceSlideDuration;
    this.autoAdvanceSlideTransitionType = autoAdvanceSlideTransitionType;
    this.__fallbackScreenShot = __fallbackScreenShot;
  }

  shiftIndex(direction: OrderShift, id: string): Screens {
    let elemIdx;
    let curr;
    let tmp;
    switch (direction) {
      case OrderShift.DOWN:
        elemIdx = this.mixedMediaObjects.findIndex((x) => x.id == id);
        if (elemIdx !== 0) {
          curr = this.mixedMediaObjects[elemIdx];
          tmp = this.mixedMediaObjects[elemIdx - 1];
          this.mixedMediaObjects[elemIdx] = tmp;
          this.mixedMediaObjects[elemIdx - 1] = curr;
        }
        break;
      case OrderShift.UP:
        elemIdx = this.mixedMediaObjects.findIndex((x) => x.id === id);
        if (elemIdx < this.mixedMediaObjects.length - 1) {
          curr = this.mixedMediaObjects[elemIdx];
          tmp = this.mixedMediaObjects[elemIdx + 1];
          this.mixedMediaObjects[elemIdx] = tmp;
          this.mixedMediaObjects[elemIdx + 1] = curr;
        }
        break;
      default:
        return this;
    }
    return this;
  }

  setMMOUrlNew(
    id: string,
    response: UploadResponse[],
    indexMap: Map<IndexName, number>
  ) {
    const idx = this.mixedMediaObjects.findIndex((ind) => ind.id === id);
    if (idx === -1) {
      Sentry.captureException(new Error("The uploaded media object was not correctly associated with an mmo"));
    }
    this.mixedMediaObjects[idx].setMMOUrl(response, indexMap);
  }

  setTransitionType(tt: number): void {
    this.autoAdvanceSlideTransitionType = tt;
  }

  setAutoAdvanceDuration(duration: number): void {
    this.autoAdvanceSlideDuration = duration;
  }

  setNotes(text: string): void {
    this.presenterNotes = text;
  }

  changeColor(color: Color): void {
    this.FlowScreenModelBackgroundColorKey = color;
  }

  setColor(color: Color): Screens {
    this.FlowScreenModelBackgroundColorKey = color;
    return this;
  }

  static fromJSON(json: any): Screens {
    let screen = Object.create(Screens.prototype);
    Object.assign(screen, json, {
      mixedMediaObjects: (json.mixedMediaObjects || []).map((mmo: any) => {
        if (mmo.type === "text") {
          return TextFlowObject.fromJSON(mmo);
        } else if (mmo.type === "shape") {
          return ShapeFlowObject.fromJSON(mmo);
        } else if (mmo.type === "image") {
          return ImageFlowObject.fromJSON(mmo);
        } else if (mmo.type === "pdf") {
          return PdfFlowObject.fromJSON(mmo);
        } else if (mmo.type === "video") {
          return VideoFlowObject.fromJSON(mmo);
        } else if (mmo.type === "gallery") {
          return GalleryFlowObject.fromJSON(mmo);
        } else if (mmo.type === "webview") {
          return WebFlowObject.fromJSON(mmo);
        } else {
          return FlowObject.fromJSON(mmo);
        }
      }),
      FlowScreenModelBackgroundColorKey: Color.fromJSON(
        json.FlowScreenModelBackgroundColorKey
      ),
      previewScreenShot: ImageAsset.fromJSON(json.previewScreenShot),
      previewScreenShot_textless: ImageAsset.fromJSON(
        json.previewScreenShot_textless
      ),
      __fallbackScreenShot: ImageAsset.fromJSON(json.previewScreenShot),
    });
    return screen;
  }

  static reviver(key: string, value: any): any {
    return key === "" ? Screens.fromJSON(value) : value;
  }

  static default(): Screens {
    return new Screens(
      new Color(222, 222, 222, 1),
      getCurrentDate(),
      guidGenerator(),
      [],
      ImageAsset.default({ __tag: "string", value: "" }),
      "no",
      ImageAsset.default({ __tag: "string", value: "" }),
      "",
      false,
      "",
      "",
      getCurrentDate(),
      ImageAsset.default({ __tag: "string", value: "" })
    );
  }

  clone(): Screens {
    let screen = Object.create(Screens.prototype);
    Object.assign(screen, this, {
      mixedMediaObjects: this.mixedMediaObjects.map((mmo: FlowObjects) => {
        mmo.__ref = undefined;
        return mmo;
      }),
    });
    return _cloneDeep(screen);
  }
}

export class UploadResponse {
  ssl_address: string;
  contentID: string;

  constructor(ssl_adress: string, treemoContentID: string) {
    this.ssl_address = ssl_adress;
    this.contentID = treemoContentID;
  }

  static fromJSON(json: any): UploadResponse {
    let ur = Object.create(UploadResponse.prototype);
    Object.assign(ur, json, {});
    return ur;
  }
}
